import { createContext, useContext } from 'react'
import type { Me, Membership } from '../api/types'

export type SessionStatus = 'loading' | 'signed-out' | 'locked' | 'signed-in'
export type Mode = 'member' | 'officer'

/** What this device remembers to reopen straight to the PIN: her number, first name and unlock token. */
export interface UnlockInfo {
  phone: string
  firstName: string
  token: string
  /** Whose figures this phone keeps for offline viewing. */
  personId?: string
}

export interface SessionValue {
  status: SessionStatus
  me: Me | null
  /** Her membership in the group she is looking at. */
  membership: Membership | null
  groupId: string | null
  mode: Mode
  /** Opened without a connection from what this phone kept: read-only until the server answers again. */
  offline: boolean
  devTools: boolean
  unlock: UnlockInfo | null
  signedIn: (me: Me, phone: string) => void
  setGroup: (groupId: string) => void
  setMode: (mode: Mode) => void
  refresh: () => Promise<void>
  signOut: () => Promise<void>
  forgetDevice: () => void
}

export const SessionContext = createContext<SessionValue | null>(null)

export function useSession(): SessionValue {
  const value = useContext(SessionContext)
  if (!value) throw new Error('useSession must be used inside <SessionProvider>')
  return value
}

/** The signed-in group and membership, for screens that only render when both exist. */
export function useGroup(): { groupId: string; membership: Membership } {
  const { groupId, membership } = useSession()
  if (!groupId || !membership) throw new Error('useGroup needs a signed-in group')
  return { groupId, membership }
}

/** Her offices in the group, without the plain "member" role: ["Accounts officer"]. */
export function officeTitles(membership: Membership | null | undefined): string[] {
  if (!membership) return []
  return membership.roles.flatMap((role, i) => (role === 'member' ? [] : [membership.role_labels[i] ?? role]))
}

/**
 * Where to go after signing in: only a path inside this app (web review finding 6). Anything else,
 * including tricks a browser reads as another site ("/\\example.com"), goes to /app.
 */
export function safeAppPath(next: string | null | undefined): string {
  if (!next || [...next].some((c) => c === '\\' || c.charCodeAt(0) < 32)) return '/app'
  try {
    const url = new URL(next, window.location.origin)
    if (url.origin !== window.location.origin || !/^\/app(\/|$)/.test(url.pathname)) return '/app'
    return url.pathname + url.search + url.hash
  } catch {
    return '/app'
  }
}
